import React, { useState, useEffect } from 'react';
import {
  Dialog,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface DemoStep {
  id: number;
  title: string;
  description: string;
  mediaUrl?: string;
  mediaType?: 'image' | 'video';
}

interface DemoModalProps {
  open: boolean;
  onClose: () => void;
  dontShowAgain: boolean;
  onDontShowAgainChange: (checked: boolean) => void;
  steps: DemoStep[];
  title?: string;
}

const DemoModal: React.FC<DemoModalProps> = ({
  open,
  onClose,
  dontShowAgain,
  onDontShowAgainChange,
  steps,
  title = 'Demo Walkthrough',
}) => {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (open) {
      setActiveStep(0);
    }
  }, [open]);

  const currentStep = steps[activeStep];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
          background: '#ffffff',
          width: '90%',
          maxWidth: '700px',
          margin: 'auto',
        },
      }}
    >
      {/* Close Button */}
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 16,
          top: 16,
          zIndex: 10,
          color: '#5a6c7d',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          '&:hover': {
            backgroundColor: '#f5f5f5',
          },
        }}
        size="small"
      >
        <CloseIcon />
      </IconButton>

      {/* Main Container with Card Style */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 0,
          minHeight: '450px',
          
          '@media (max-width: 600px)': {
            gridTemplateColumns: '1fr',
            minHeight: 'auto',
          },
        }}
      >
        {/* Left: Image with Background */}
        {currentStep?.mediaUrl && (
          <Box
            sx={{
              backgroundImage: `url(${currentStep.mediaUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: '12px 0 0 12px',
              position: 'relative',
              overflow: 'hidden',
              animation: 'fadeInLeft 0.6s ease-in-out',
              
              '@keyframes fadeInLeft': {
                from: {
                  opacity: 0,
                  transform: 'translateX(-30px)',
                },
                to: {
                  opacity: 1,
                  transform: 'translateX(0)',
                },
              },
              
              '@media (max-width: 600px)': {
                borderRadius: '12px 12px 0 0',
                minHeight: '280px',
              },
            }}
          >
            {/* Overlay for better text contrast */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(0, 56, 131, 0.15) 0%, rgba(0, 0, 0, 0.1) 100%)',
              }}
            />
          </Box>
        )}

        {/* Right: Content Section */}
        <Box
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            background: '#ffffff',
            borderRadius: '0 12px 12px 0',
            animation: 'fadeInRight 0.6s ease-in-out 0.1s backwards',
            
            '@keyframes fadeInRight': {
              from: {
                opacity: 0,
                transform: 'translateX(30px)',
              },
              to: {
                opacity: 1,
                transform: 'translateX(0)',
              },
            },
            
            '@media (max-width: 600px)': {
              borderRadius: '0 0 12px 12px',
              p: 3,
            },
          }}
        >
          {/* Modal Title Header */}
          <Typography
            sx={{
              fontSize: '0.85rem',
              fontWeight: 600,
              color: '#003883',
              mb: 2,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              opacity: 0.8,
            }}
          >
            {title}
          </Typography>

          {/* Step Title */}
          <Typography
            sx={{
              fontSize: '1.6rem',
              fontWeight: 700,
              color: '#003883',
              mb: 2,
              lineHeight: 1.3,
              animation: 'fadeInUp 0.6s ease-in-out 0.2s backwards',
              
              '@keyframes fadeInUp': {
                from: {
                  opacity: 0,
                  transform: 'translateY(15px)',
                },
                to: {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}
          >
            {currentStep?.title}
          </Typography>

          {/* Description */}
          <Typography
            sx={{
              fontSize: '0.95rem',
              color: '#5a6c7d',
              lineHeight: 1.7,
              mb: 3,
              flex: 1,
              animation: 'fadeInUp 0.6s ease-in-out 0.3s backwards',
              
              '@keyframes fadeInUp': {
                from: {
                  opacity: 0,
                  transform: 'translateY(15px)',
                },
                to: {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}
          >
            {currentStep?.description}
          </Typography>

          {/* Don't Show Again Checkbox */}
          <Box
            sx={{
              mt: 'auto',
              pt: 3,
              borderTop: '1px solid #e0e0e0',
              animation: 'fadeIn 0.6s ease-in-out 0.4s backwards',
              
              '@keyframes fadeIn': {
                from: {
                  opacity: 0,
                },
                to: {
                  opacity: 1,
                },
              },
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={dontShowAgain}
                  onChange={(e) => onDontShowAgainChange(e.target.checked)}
                  sx={{
                    color: '#003883',
                    '&.Mui-checked': {
                      color: '#003883',
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: '20px',
                    },
                  }}
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{
                    color: '#5a6c7d',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    ml: 0.5,
                  }}
                >
                  Don't show this again
                </Typography>
              }
            />
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default DemoModal;
